import { apiClient } from '@devd-client/api';
import { getToken, getOrgId, getUserId } from '@devd-client/devd/auth';
import type {
  TeamDetailsResponse,
  TeamGoalsResponse,
  ContributorTableMetricsForTeamsResponse,
  ContributorTableMetricsResponse,
  LanguagesListResponse,
  TeamMetricsResponse,
  MetricsListResponse,
  TeamComparisonTeamsListResponse,
} from '../helpers/team-details.types';
import {
  TeammenuResponse,
  RepoListResponse,
  PageDetailsDto,
  ApiResponse,
  JobRolesListResponse,
} from '@devd-client/devd/components';

export const getTeamDetails = (team: string): Promise<TeamDetailsResponse> =>
  apiClient(`/v1/team/${team}/org/${getOrgId()}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      authorization: `bearer ${getToken()}`,
    },
  });

export const getAllGoals = (team: string): Promise<TeamGoalsResponse> =>
  apiClient(`/v1/goal/org/${getOrgId()}${team && `?team=${team}`}`, {
    method: 'GET',
    headers: {
      authorization: `bearer ${getToken()}`,
    },
  });
export const getTeammenu = (): Promise<TeammenuResponse> =>
  apiClient(
    `/v2/team/${getOrgId()}/lite${
      /^\/contributors\/$/.test(window.location.pathname)
        ? `?employee=${window.location.pathname?.split('/')?.pop()}`
        : ''
    }`,
    {
      method: 'GET',
      headers: {
        authorization: `bearer ${getToken()}`,
      },
    }
  );

export const postContributorTableMetricsForTeams = ({
  team,
  metrics,
}: {
  team: string;
  metrics: string[];
}) => {
  return apiClient(
    `/v1/team/org/${getOrgId()}/teamName/${team}/contributorTableMetrics`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `bearer ${getToken()}`,
      },
      body: JSON.stringify({ metrics }),
    }
  );
};

export const getContributorTableMetricsForTeams = (
  team: string
): Promise<ContributorTableMetricsForTeamsResponse> =>
  apiClient(
    `/v1/team/org/${getOrgId()}/teamName/${team}/contributorTableMetrics`,
    {
      method: 'GET',
      headers: {
        authorization: `bearer ${getToken()}`,
      },
    }
  );

export const postContributorTableCsv = (params: {
  filter: {
    orgId: string;
    startDate: string;
    endDate: string;
    sprintId: string;
    teamId: string;
    projectsFilter: string[];
    searchTerm: string;
    contributorFilters: {
      prog_lang: string;
      activeFilters: { field: string; value: string } | null;
      inActiveFilters: { field: string; value: string } | null;
      excluded: boolean;
    };
  };
  metricList: string[];
}): Promise<any> => {
  return apiClient(`/v1/contributorTable/org/${getOrgId()}/csv`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      authorization: `bearer ${getToken()}`,
    },
    body: JSON.stringify(params),
  });
};
export const getContributorTableMetrics =
  (): Promise<ContributorTableMetricsResponse> =>
    apiClient(`/v1/contributorTable/metrics/${getOrgId()}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        authorization: `bearer ${getToken()}`,
      },
    });
export const getAllRepos = (source: string): Promise<RepoListResponse> =>
  apiClient(
    `/v1/integration/${source}/org/${getOrgId()}/projects/lite?active=true`,
    {
      method: 'GET',
      headers: {
        authorization: `bearer ${getToken()}`,
      },
    }
  );
export const getAllLanguages = (): Promise<LanguagesListResponse> =>
  apiClient(`/v1/contributorTable/proglangs/${getOrgId()}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      authorization: `bearer ${getToken()}`,
    },
  });
export const updateGoalSettings = (params: any): Promise<any> => {
  const { team, teams, ...data } = params;
  return apiClient(`/v1/goal/org/${getOrgId()}/team/${params.team}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: `bearer ${getToken()}`,
    },
    body: JSON.stringify(data),
  });
};

export const getAllTeamMetrics = (team: string): Promise<TeamMetricsResponse> =>
  apiClient(`/v1/team/metric/org/${getOrgId()}/team/${team}/fav`, {
    method: 'GET',
    headers: {
      authorization: `bearer ${getToken()}`,
    },
  });

export const postFavMetrics = (params: any) => {
  return apiClient(`/v1/team/metric/org/${getOrgId()}/team/${params.team}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: `bearer ${getToken()}`,
    },
    body: JSON.stringify(params.metricsList),
  });
};

export const getTeamsPage = (): Promise<ApiResponse<PageDetailsDto>> =>
  apiClient(`/v2/account/org/${getOrgId()}/page/Teams`, {
    method: 'GET',
    headers: {
      authorization: `bearer ${getToken()}`,
    },
  });

export const getTeamPage = (): Promise<ApiResponse<PageDetailsDto>> =>
  apiClient(`/v2/account/org/${getOrgId()}/page/Team`, {
    method: 'GET',
    headers: {
      authorization: `bearer ${getToken()}`,
    },
  });

export const getMetricsList = (): Promise<MetricsListResponse> =>
  apiClient(`/v1/teamComparison/metrics/${getOrgId()}`, {
    method: 'GET',
    headers: {
      authorization: `bearer ${getToken()}`,
    },
  });

export const getTeamComparisonTeamsList =
  (): Promise<TeamComparisonTeamsListResponse> =>
    apiClient(`/v1/teamComparison/teams/${getOrgId()}`, {
      method: 'GET',
      headers: {
        authorization: `bearer ${getToken()}`,
      },
    });

export const postTeamMetrics = (params: any) => {
  return apiClient(`/v1/user/${getUserId()}/preference/teams_metrics`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: `bearer ${getToken()}`,
    },
    body: JSON.stringify(params),
  });
};

export const getJobRolesList = (): Promise<JobRolesListResponse> =>
  apiClient(`/v1/job-roles/org/${getOrgId()}`, {
    method: 'GET',
    headers: {
      authorization: `bearer ${getToken()}`,
    },
  });
